import EXIF from 'exif-js';

type CanvasImageSource = HTMLOrSVGElement | HTMLVideoElement | HTMLCanvasElement | ImageBitmap;
interface BlobCallback {
  (blob: Blob | null): void;
}

export const imgToCanvasWithOrientation = (
  img: CanvasImageSource,
  rawWidth: number,
  rawHeight: number,
  orientation: number
) => {
  const canvas = document.createElement('canvas');

  if (orientation > 4) {
    canvas.width = rawHeight;
    canvas.height = rawWidth;
  } else {
    canvas.width = rawWidth;
    canvas.height = rawHeight;
  }

  const ctx = canvas.getContext('2d');

  switch (orientation) {
    case 2:
      ctx?.transform(-1, 0, 0, 1, rawWidth, 0);
      break;
    case 3:
      ctx?.transform(-1, 0, 0, -1, rawWidth, rawHeight);
      break;
    case 4:
      ctx?.transform(1, 0, 0, -1, 0, rawHeight);
      break;
    case 5:
      ctx?.transform(0, 1, 1, 0, 0, 0);
      break;
    case 6:
      ctx?.transform(0, 1, -1, 0, rawHeight, 0);
      break;
    case 7:
      ctx?.transform(0, -1, -1, 0, rawHeight, rawWidth);
      break;
    case 8:
      ctx?.transform(0, -1, 1, 0, 0, rawWidth);
      break;
    default:
      break;
  }

  ctx?.drawImage(img, 0, 0, rawWidth, rawHeight);
  return canvas;
};

/**
 * This function reduces the size of an image file object. The reduced file object
 * passed onto the callback function. The EXIF data gets lost in the process, but
 * this function rotates the image based on EXIF data before it gets lost.
 *
 * @param {Object} file - Image file obj to reduce size.
 * @param {Integer} fileSizeThreshold
 * @param {Integer} maxWidth
 * @param {Integer} maxHeight
 * @param {Float} quality - Quality of the output image, represented by a float ranging from 0 - 1.
 * @return {Function} - Callback function that takes in a reduced blob of the input file object.
 */
// @ts-ignore
export const reduceImageSize = (
  file: File | null,
  fileSizeThreshold: number,
  maxWidth: number,
  maxHeight: number,
  quality: number,
  callback: BlobCallback
) => {
  if (!file || file.size <= fileSizeThreshold) {
    callback(file);
    return;
  }

  const img = new Image();
  const url = URL.createObjectURL(file);
  img.src = url;
  img.onerror = () => {
    URL.revokeObjectURL(url);
    callback(file);
  };

  img.onload = () => {
    URL.revokeObjectURL(url);
    let orientation = 0;
    // get the image orientation from the metadata
    // (library has inaccurate typing and is no longer maintained)
    // TODO: revist usage of this exif-js library for image metadata
    // eslint-disable-next-line
    EXIF.getData(file, () => {
      orientation = EXIF.getTag(img, 'Orientation');
    });
    // need to scale based on longest dimension (vertical or horizontal)
    const scale =
      orientation > 4
        ? Math.min(maxHeight / img.width, maxWidth / img.height, 1)
        : Math.min(maxWidth / img.width, maxHeight / img.height, 1);
    const h = Math.round(img.height * scale);
    const w = Math.round(img.width * scale);
    const canvas = imgToCanvasWithOrientation(img, w, h, orientation);
    canvas.toBlob(callback, 'image/jpeg', quality);
  };
};
